// « CAPTURER » — le bouton rond au centre de la barre du bas, en mode mobile.
//
// Sur le terrain : on touche, on choisit l'étude (la dernière ouverte est
// proposée), on prend la photo, la vidéo, la voix ou la note. Deux gestes, et
// la pièce est rangée dans l'étude.
import { useState } from 'react';
import { useStudies } from '../api/queries';
import { Button, Select, Spinner } from '../components/ui';
import { Icon } from '../components/icons';
import { savePreference } from '../legal/cookieConsent';
import { useRouter } from '../router';
import { presetTab } from '../components/Tabs';
import { Drawer } from '../write/Drawer';
import { useFieldItems, type FieldItem } from './api';
import { FieldCapture } from './FieldCapture';
import './capture.css';

const KEY = (projectId: string) => `ro.lastStudy.${projectId}`;
const memory = new Map<string, string>();

/** Retient la dernière étude ouverte d'un projet (préférence d'affichage). */
export function rememberStudy(projectId: string, studyId: string) {
  memory.set(projectId, studyId);
  savePreference(KEY(projectId), studyId);
}

function lastStudy(projectId: string): string | null {
  if (memory.has(projectId)) return memory.get(projectId) ?? null;
  try {
    return localStorage.getItem(KEY(projectId));
  } catch {
    return null;
  }
}

const studyPath = (projectId: string, studyId: string) =>
  `/projet/${encodeURIComponent(projectId)}/etudes/${encodeURIComponent(studyId)}`;

function CaptureSheet({ projectId, onClose }: { projectId: string; onClose: () => void }) {
  const studies = useStudies(projectId);
  const { navigate } = useRouter();
  const list = studies.data ?? [];
  const remembered = lastStudy(projectId);
  const [chosen, setChosen] = useState<string | null>(null);
  const studyId = chosen ?? (list.some((s) => s.id === remembered) ? remembered : list[0]?.id ?? null);
  const items = useFieldItems(studyId);
  const [added, setAdded] = useState<FieldItem | null>(null);
  const study = list.find((s) => s.id === studyId);

  if (studies.isPending) return <Spinner label="Chargement des études" />;
  if (!list.length) {
    return <p className="card-note">Créez d’abord une étude : les pièces terrain s’y rangent.</p>;
  }

  if (added && studyId) {
    return (
      <div className="field-capture">
        <p className="zy-texte-muet" role="status">
          Ajouté à l’étude {study?.code ?? ''} {study?.objective ? `· ${study.objective}` : ''}.
        </p>
        <div className="capture-or">
          <Button variant="accent" onClick={() => setAdded(null)}>Capturer encore</Button>
          <Button
            variant="ghost"
            onClick={() => {
              onClose();
              presetTab('etude', 'capter');
              navigate(studyPath(projectId, studyId));
            }}
          >
            Voir dans l’étude
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="field-capture">
      <label className="zy-field">
        <span className="zy-field__label">Étude</span>
        <Select value={studyId ?? ''} onChange={(e) => setChosen(e.target.value)}>
          {list.map((s) => (
            <option key={s.id} value={s.id}>
              {[s.code, s.objective].filter(Boolean).join(' · ') || s.id}
            </option>
          ))}
        </Select>
      </label>
      {studyId ? (
        <FieldCapture
          key={studyId}
          studyId={studyId}
          uploads={items.data?.uploads ?? true}
          onDone={(item) => {
            rememberStudy(projectId, studyId);
            setAdded(item);
          }}
        />
      ) : null}
    </div>
  );
}

export function CaptureButton({ projectId }: { projectId: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="mobile-capture" aria-label="Capturer : photo, vidéo, voix ou note" onClick={() => setOpen(true)}>
        <span className="mobile-capture-disc">
          <Icon name="camera" size={22} />
        </span>
      </button>
      <Drawer open={open} title="Capturer" description="Photo, vidéo, voix ou note, rangée dans l’étude." onClose={() => setOpen(false)}>
        {open ? <CaptureSheet projectId={projectId} onClose={() => setOpen(false)} /> : null}
      </Drawer>
    </>
  );
}
